import React from 'react'
import {Typography} from '@mui/material'
import moment from 'moment'
import {ColumnDef} from 'src/components-new/data/DataTable'
import {FilterItem} from 'src/components-new/data/Filters'
import StatusPill from 'src/components-new/primitives/StatusPill'

export interface QuestionnaireRow {
  _id: string
  name?: string
  state?: string
  translatedState?: string
  [key: string]: any
}

export interface QuestionnaireColumnConfig {
  id: string
  label: string
  type?: string
  format?: string
}

// Same mapping as the legacy page (src/pages/surveys/Surveys.tsx) and the new-style
// per-patient tab (src/pages-new/patient/tabs/SurveysTab.tsx) — kept in sync across
// all three since the backend's state enum is fixed.
export const QUESTIONNAIRE_STATE_LABELS: Record<string, string> = {
  CONFIRMED: 'Confermato',
  COMPILED: 'Compilato',
  TO_COMPILE: 'Da compilare',
}

export function getQuestionnaireStateColor(state: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (state === 'CONFIRMED') return 'success'
  if (state === 'COMPILED') return 'info'
  if (state === 'TO_COMPILE') return 'warning'
  return 'default'
}

// Same id/label pairs as the legacy DEFAULT_COLUMNS.
export const DEFAULT_COLUMNS: QuestionnaireColumnConfig[] = [
  {id: 'name', label: 'NOME'},
  {id: 'translatedState', label: 'STATO'},
  {id: 'createdAt', label: 'DATA CREAZIONE', type: 'date', format: 'ddd DD MMM YYYY'},
  {id: 'requestDate', label: 'DATA COMPILAZIONE', type: 'date', format: 'ddd DD MMM YYYY'},
]

const QUESTIONNAIRE_STATE_OPTIONS = Object.entries(QUESTIONNAIRE_STATE_LABELS).map(([value, label]) => ({value, label}))

// PSP-driven columnOptions only ever carry {id, label} (see the DB shape driving this page) —
// never `type`/`format` — so date-ness is inferred from the known field ids instead of
// trusting col.type, which columnOptions never sets.
const DATE_COLUMN_IDS = new Set(['createdAt', 'requestDate'])

// Same columnId-mirroring pattern as SurveysTab.tsx: a filter only shows up if its mirrored
// column is active, so hiding e.g. the STATO column also hides the Stato filter.
export const ALL_QUESTIONNAIRE_FILTERS: Array<FilterItem & {columnId: string}> = [
  {id: 'name', columnId: 'name', label: 'Nome', type: 'string'},
  {id: 'state', columnId: 'translatedState', label: 'Stato', type: 'select', options: QUESTIONNAIRE_STATE_OPTIONS},
  {id: 'requestDate', columnId: 'requestDate', label: 'Data di compilazione', type: 'rangePicker'},
]

/**
 * Columns are PSP-driven (`surveysConfiguration.columnOptions`), falling back to a
 * sensible default set — mirrors the pattern in medicalCentersColumns.tsx. `name`
 * renders as the mobile card header and `state` as its badge.
 */
export function buildColumns(columnConfig?: QuestionnaireColumnConfig[]): ColumnDef<QuestionnaireRow>[] {
  const config = columnConfig && columnConfig.length > 0 ? columnConfig : DEFAULT_COLUMNS
  return config.map(col => {
    if (col.id === 'translatedState' || col.id === 'state') {
      return {
        key: col.id,
        header: col.label,
        mobileCardBadge: true,
        render: (row: QuestionnaireRow) => (
          <StatusPill
            label={QUESTIONNAIRE_STATE_LABELS[row.state ?? ''] ?? row.state ?? '—'}
            color={getQuestionnaireStateColor(row.state)}
          />
        ),
      }
    }

    return {
      key: col.id,
      header: col.label,
      mobileCardHeader: col.id === 'name',
      render: (row: QuestionnaireRow) => {
        if (DATE_COLUMN_IDS.has(col.id) && row[col.id]) {
          return <Typography variant="body2">{moment(row[col.id]).format(col.format ?? 'ddd DD MMM YYYY')}</Typography>
        }
        return <Typography variant="body2">{row[col.id] ?? '—'}</Typography>
      },
    }
  })
}
